import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import InfoIcon from '@material-ui/icons/Info';
import { makeStyles } from '@material-ui/core/styles';
import { bool, string } from 'prop-types';
import { Trans } from '@lingui/macro';
import ClassifierLogTable from './ClassifierLogTable';
import { isNewDevice, SERVER } from '../InfrastructureConstants';
import { isDownState, isNewState, isPolled, isUnmonitoredState } from './DeviceHelper';

const useStyles = makeStyles(() => ({
    messageContainer: {
        padding: '10px 10px 0 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    icon: {
        width: 42,
        marginBottom: 10,
        color: '#EB7861',
        fontSize: 42,
    },
    messageList: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    message: {
        fontWeight: 500,
        color: '#EB7861',
        lineHeight: '24px',
    },
}));

/**
 * Returns array of help messages based on device state and type.
 * @param {string} uiState - Device UI state
 * @param {string} deviceType - Device type
 * @param {boolean} isServer - deviceType === SERVER
 * @returns {Array<JSX.Element>} Array of help messages
 */
export const getHelpMessages = (uiState, deviceType, isServer) => {
    const messages = [];
    const isNewOrDown = isNewState(uiState) || isDownState(uiState);
    const isServerOrNewDevice = isServer || isNewDevice(deviceType);

    // First message: SNMP/WinRM check
    if (isNewOrDown && isServerOrNewDevice) {
        messages.push(
            <Trans key="snmp-winrm">Please check if SNMP or WinRM is supported and enabled on the device</Trans>
        );
    } else if (isNewOrDown) {
        messages.push(<Trans key="snmp">Please check if SNMP is supported and enabled on the device</Trans>);
    }

    // Second message: Credential check (only for non-unmonitored states)
    if (isNewOrDown) {
        messages.push(
            <Trans key="credential">Please check if the credential for the device is configured correctly</Trans>
        );
    }

    // Third message: Unmonitored device
    if (isUnmonitoredState(uiState) && isNewDevice(deviceType)) {
        messages.push(
            <Trans key="unmonitored">
                We cannot poll performance data from this device because it does not have host MIB, interface MIB or
                supported vendor specific MIBs.
            </Trans>
        );
    }

    return messages;
};

const DiagnosticsMessage = ({ uiState, deviceType }) => {
    const classes = useStyles();
    const isServer = deviceType === SERVER;
    const shouldShowMessages = isPolled(deviceType);
    const helpMessages = getHelpMessages(uiState, deviceType, isServer);
    const shouldRenderContainer =
        shouldShowMessages &&
        helpMessages.length > 0 &&
        (isNewState(uiState) || isDownState(uiState) || (isUnmonitoredState(uiState) && isNewDevice(deviceType)));

    if (!shouldRenderContainer) {
        return null;
    }

    return (
        <Box className={classes.messageContainer}>
            <InfoIcon className={classes.icon} />
            <Box className={classes.messageList}>
                {helpMessages.map((message) => (
                    <Typography key={message.key} className={classes.message}>
                        {message}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

DiagnosticsMessage.propTypes = {
    uiState: string.isRequired,
    deviceType: string.isRequired,
};

const MonitoringDeviceDiagnosticsSection = ({ id, uiState, deviceType, showMessage }) => {
    return (
        <>
            <ClassifierLogTable _id={id} />
            {showMessage && <DiagnosticsMessage uiState={uiState} deviceType={deviceType} />}
        </>
    );
};

MonitoringDeviceDiagnosticsSection.propTypes = {
    id: string.isRequired,
    uiState: string.isRequired,
    deviceType: string.isRequired,
    showMessage: bool.isRequired,
};

export default MonitoringDeviceDiagnosticsSection;
